import React, { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import RazorpayCheckoutButton from "../components/RazorpayCheckoutButton";

// DEV/TEST ONLY — hidden from navigation. Validates the billing plumbing
// (create → Checkout → verify → canonical entitlement) before the real
// Plans & Billing UI is built into the Boardroom. Not polished on purpose.

const inr = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN")}`;

const PLAN_MODE = { trial: "trial", monthly: "subscription", yearly: "subscription", lifetime: "order" };

export default function BillingTest() {
  const [plans, setPlans] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [ent, setEnt] = useState(null);
  const [entRaw, setEntRaw] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [result, setResult] = useState(null);

  const loadMe = useCallback(async () => {
    const me = await api.billingMe();
    if (me.ok) { setEnt(me.data.entitlement); setEntRaw(me.data); }
    else setEntRaw({ error: me.data?.detail || me.error });
  }, []);

  const load = useCallback(async () => {
    const p = await api.billingPlans();
    if (p.ok) { setPlans(p.data.plans || []); setCfg(p.data); }
    loadMe();
  }, [loadMe]);

  useEffect(() => { load(); }, [load]);

  const validateCoupon = async (planCode) => {
    setCouponMsg(null);
    const res = await api.billingValidateCoupon(planCode, coupon);
    const d = res.data || {};
    setCouponMsg(d.valid
      ? { ok: true, text: `Valid — you save ${inr(d.discount_paise)} (final ${inr(d.final_amount_paise)})` }
      : { ok: false, text: `Invalid: ${d.reason || res.error}` });
  };

  const onResult = (r) => {
    setResult(r);
    if (r.stage === "verified" && r.ok) loadMe();
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl space-y-6" data-testid="billing-test-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-600">Dev / Test only</div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">C-ORE Billing — Plumbing Test</h1>
        </div>
        <button onClick={load} data-testid="billing-refresh" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-[13px] hover:bg-slate-50">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {cfg && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-[12.5px]" data-testid="billing-config">
          <div className="font-semibold text-[#1E3A5F] mb-1">Razorpay config</div>
          <div>Key configured: <b className={cfg.razorpay?.key_configured ? "text-emerald-600" : "text-rose-600"}>{String(!!cfg.razorpay?.key_configured)}</b></div>
          <div>Webhook secret: <b className={cfg.razorpay?.webhook_secret_configured ? "text-emerald-600" : "text-amber-600"}>{String(!!cfg.razorpay?.webhook_secret_configured)}</b></div>
          {cfg.missing_recurring_plan_env?.length > 0 && (
            <div className="mt-1 text-amber-700">Missing recurring plan env: {cfg.missing_recurring_plan_env.join(", ")}</div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-entitlement">
        <div className="font-semibold text-[#1E3A5F] mb-1 text-[13px]">Canonical entitlement (from backend)</div>
        <pre className="text-[12px] bg-slate-50 rounded p-3 overflow-auto">{JSON.stringify(ent || entRaw, null, 2)}</pre>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="billing-coupon">
        <div className="font-semibold text-[#1E3A5F] text-[13px]">Coupon (server-validated)</div>
        <div className="flex items-center gap-2">
          <input data-testid="billing-coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="e.g. WELCOME10 / FLAT500 / EXPIRED50"
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] w-72" />
          <button onClick={() => validateCoupon("lifetime")} data-testid="billing-coupon-check" className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] hover:bg-slate-50">Check on Lifetime</button>
        </div>
        {couponMsg && <div className={`text-[12.5px] ${couponMsg.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="billing-coupon-msg">{couponMsg.text}</div>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.code} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-3" data-testid={`billing-plan-${p.code}`}>
            <div>
              <div className="text-[13.5px] font-bold text-[#1E3A5F]">{p.name}</div>
              <div className="text-[20px] font-bold text-[#1E3A5F]">{p.code === "trial" ? "Free" : inr(p.amount_paise)}</div>
              <div className="text-[11.5px] text-slate-500">
                {p.code === "trial" ? `${p.trial_days} days` : p.billing_type === "recurring" ? `per ${p.interval}, auto-renews` : "one-time, lifetime"}
              </div>
            </div>
            <RazorpayCheckoutButton
              mode={PLAN_MODE[p.code]}
              planCode={p.code}
              couponCode={coupon}
              testid={`billing-pay-${p.code}`}
              label={p.code === "trial" ? "Start trial" : p.billing_type === "recurring" ? "Subscribe" : "Buy lifetime"}
              onResult={onResult}
            />
          </div>
        ))}
      </div>

      {ent?.entitlement_status === "active" && !ent?.lifetime && !ent?.plan_code?.includes("trial") && (
        <button onClick={async () => { const r = await api.billingSubscriptionCancel(); setResult({ stage: "cancel", ...r }); loadMe(); }}
          data-testid="billing-cancel" className="rounded-lg border border-rose-200 text-rose-700 px-4 py-2 text-[13px] hover:bg-rose-50">
          Cancel auto-renewal
        </button>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-result">
        <div className="font-semibold text-[#1E3A5F] mb-1 text-[13px]">Last checkout / verification result</div>
        <pre className="text-[12px] bg-slate-50 rounded p-3 overflow-auto min-h-[60px]">{result ? JSON.stringify(result, null, 2) : "—"}</pre>
      </div>
    </div>
  );
}
